import { buscarEmpreendimentoByIdService } from "../../../services/buscarEmpreendimentoByIdService";

export type Compatibilidade = {
  id: number;
  origemProcura: string;
  procuraId: number;
  origemDisp: string;
  dispId: number;
  tipoInteresseProcura: string;
  tipoInteresseDisponivel: string;
  nivel: number;
  valorProcura: string;
  valorDisp: string;
  dataIniProcura: string;
  dataFimProcura: string;
  dataIniDisp: string;
  dataFimDisp: string;
  empreendId: number;
  usuarioProcura?: any;
  usuarioDisp?: any;
  [key: string]: any;
};

export type Agrupado = {
  procuraId: number;
  origemProcura: string;
  tipoInteresseProcura: string;
  valorProcura: string;
  dataIniProcura: string;
  dataFimProcura: string;
  nomeEmpreendimento: string;
  filhos: (Compatibilidade & { nivel: number })[];
};

export type ProcuraSemCompatibilidade = {
  chave: string;
  procuraId: number;
  origemProcura: string;
  tipoInteresseProcura: string;
  valorProcura: string | number;
  dataIniProcura: string;
  dataFimProcura: string;
  nomeEmpreendimento: string;
  empreendId: number;
  usuarioProcura?: string;
  [key: string]: any;
};

export type TotaisSemCompatibilidade = {
  total: number;
  totalINT: number;
  totalPOST: number;
};

const formatarNomeCompleto = (usuario: any): string => {
  if (!usuario) return "Desconhecido";
  if (typeof usuario === "string") return usuario;

  const nomeCompleto = `${usuario.nome || ""} ${usuario.sobrenome || ""}`.trim();
  return nomeCompleto || "Desconhecido";
};

const getPrimeiroValor = (item: Record<string, any>, chaves: string[]) => {
  for (const chave of chaves) {
    const valor = item?.[chave];
    if (valor !== undefined && valor !== null && valor !== "") {
      return valor;
    }
  }

  return undefined;
};

const getTipoInteresseProcura = (item: Record<string, any>) => {
  const tipoInteresse = getPrimeiroValor(item, [
    "tipoInteresseProcura",
    "tipoInteresse",
  ]);

  if (tipoInteresse) {
    return tipoInteresse;
  }

  const alugarAtivo = item?.alugar === 1 || item?.alugar === true;
  const trocarAtivo = item?.trocar === 1 || item?.trocar === true;

  if (alugarAtivo && trocarAtivo) return "ALUGAR_TROCAR";
  if (alugarAtivo) return "ALUGAR";
  if (trocarAtivo) return "TROCAR";

  return "NAO_DEFINIDO";
};

const normalizarProcuraSemCompatibilidade = (
  item: Record<string, any>,
  empreendimentosMap: Map<number, any>
): ProcuraSemCompatibilidade => {
  const procuraId = Number(getPrimeiroValor(item, ["procuraId", "id"]) || 0);
  const empreendId = Number(getPrimeiroValor(item, ["empreendId"]) || 0);
  const origemProcura = String(
    getPrimeiroValor(item, ["origemProcura", "origem"]) || ""
  );
  const usuarioProcuraOriginal =
    getPrimeiroValor(item, ["usuarioProcura", "usuario", "UsuarioTable"]) ||
    (item?.nome || item?.sobrenome
      ? { nome: item.nome, sobrenome: item.sobrenome }
      : undefined);

  return {
    ...item,
    chave: `${origemProcura || "SEM_ORIGEM"}-${procuraId}-${empreendId}`,
    procuraId,
    origemProcura,
    tipoInteresseProcura: getTipoInteresseProcura(item),
    valorProcura: getPrimeiroValor(item, ["valorProcura", "valor"]) || 0,
    dataIniProcura:
      String(
        getPrimeiroValor(item, ["dataIniProcura", "dataInicio", "dataIni"]) ||
          ""
      ) || "",
    dataFimProcura:
      String(
        getPrimeiroValor(item, ["dataFimProcura", "dataFim", "dataFinal"]) ||
          ""
      ) || "",
    nomeEmpreendimento:
      getPrimeiroValor(item, ["nomeEmpreendimento", "empreendimentoNome"]) ||
      empreendimentosMap.get(empreendId)?.nome ||
      "Desconhecido",
    empreendId,
    usuarioProcura: formatarNomeCompleto(usuarioProcuraOriginal),
  };
};

export const agruparCompatibilidadesPorProcura = (
  data: Compatibilidade[],
  empreendimentosMap: Map<number, any>
): (Agrupado & {
  chave: string;
  empreendId: number;
  usuarioProcura?: string;
})[] => {
  const mapa = new Map<
    string,
    Agrupado & { chave: string; empreendId: number; usuarioProcura?: string }
  >();

  for (const item of data) {
    const {
      procuraId,
      origemProcura,
      tipoInteresseProcura,
      valorProcura,
      dataIniProcura,
      dataFimProcura,
      nivel,
      empreendId,
      usuarioProcura,
      usuarioDisp,
      nomeEmpreendimento,
    } = item;

    const chave = `${procuraId}-${empreendId}`;

    if (!mapa.has(chave)) {
      mapa.set(chave, {
        chave,
        empreendId,
        procuraId,
        origemProcura,
        tipoInteresseProcura,
        valorProcura,
        dataIniProcura,
        dataFimProcura,
        filhos: [],
        nomeEmpreendimento:
          nomeEmpreendimento ||
          empreendimentosMap.get(empreendId)?.nome ||
          "Desconhecido",
        usuarioProcura: formatarNomeCompleto(usuarioProcura),
      });
    }

    mapa.get(chave)?.filhos.push({
      ...item,
      nivel,
      usuarioDisp: formatarNomeCompleto(usuarioDisp),
    });
  }

  mapa.forEach((agrupado) => {
    agrupado.filhos.sort((a, b) => a.nivel - b.nivel);
  });

  return Array.from(mapa.values()).sort((a, b) =>
    (a.usuarioProcura || "").localeCompare(b.usuarioProcura || "")
  );
};

export const montarCompatibilidadesAgrupadas = async (
  data: Compatibilidade[]
) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const empreendimentosUnicos = Array.from(
    new Set(data.map((item) => item.empreendId).filter(Boolean))
  );

  const empreendimentosMap = new Map<number, any>();

  for (const id of empreendimentosUnicos) {
    const empreend = await buscarEmpreendimentoByIdService(id);
    empreendimentosMap.set(id, empreend);
  }

  return agruparCompatibilidadesPorProcura(data, empreendimentosMap);
};

export const montarProcurasSemCompatibilidades = async (
  data: Record<string, any>[]
) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const empreendimentosUnicos = Array.from(
    new Set(
      data
        .map((item) => Number(getPrimeiroValor(item, ["empreendId"]) || 0))
        .filter(Boolean)
    )
  );

  const empreendimentosMap = new Map<number, any>();

  for (const id of empreendimentosUnicos) {
    const empreend = await buscarEmpreendimentoByIdService(id);
    empreendimentosMap.set(id, empreend);
  }

  return data
    .map((item) => normalizarProcuraSemCompatibilidade(item, empreendimentosMap))
    .sort((a, b) =>
      (a.usuarioProcura || "").localeCompare(b.usuarioProcura || "")
    );
};

export const resumirProcurasSemCompatibilidades = (
  procuras: ProcuraSemCompatibilidade[]
): TotaisSemCompatibilidade => {
  const chavesUnicas = new Set(
    procuras.map((item) => `${item.origemProcura}-${item.procuraId}`)
  );
  const interessesUnicos = new Set(
    procuras
      .filter((item) => item.origemProcura === "INT")
      .map((item) => item.procuraId)
  );
  const postsUnicos = new Set(
    procuras
      .filter((item) => item.origemProcura === "POST")
      .map((item) => item.procuraId)
  );

  return {
    total: chavesUnicas.size,
    totalINT: interessesUnicos.size,
    totalPOST: postsUnicos.size,
  };
};
